import { useEffect, useState } from "react";

import { api, errorMessage } from "../api/client";
import { formatCount, formatDuration } from "../lib/format";
import { ui } from "../lib/ui";
import { urlParam, useUrlState } from "../lib/urlState";
import { useAsync } from "../lib/useAsync";
import { useTransientStatus } from "../lib/useTransientStatus";
import type { Branch, PersonTrack, TrackDayTotals } from "../types";
import { AsyncBlock } from "./AsyncBlock";
import { Panel } from "./Panel";
import { Select } from "./Select";

const PAGE_SIZE = 100;

const cameraLabel = (cameraId: string) =>
  cameraId === "__entrance__" ? "Entrance" : cameraId;

const timeOnly = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const AGE_LABEL: Record<string, string> = {
  age_0_17: "0–17",
  age_18_29: "18–29",
  age_30_44: "30–44",
  age_45_plus: "45+",
};

function Crossing({ track }: { track: PersonTrack }) {
  if (!track.entered_at && !track.exited_at) {
    return <span className="text-muted">—</span>;
  }
  const countedAs =
    track.gender || track.age_bucket
      ? `${track.gender ?? "?"} ${AGE_LABEL[track.age_bucket ?? ""] ?? "?"}`
      : track.entered_at
        ? "unknown demographics"
        : null;
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {track.entered_at && (
          <span className={`${ui.pill} ${ui.pillSucceeded}`} title={track.entered_at}>
            entry {timeOnly(track.entered_at)}
          </span>
        )}
        {track.exited_at && (
          <span className={`${ui.pill} ${ui.pillRunning}`} title={track.exited_at}>
            exit {timeOnly(track.exited_at)}
          </span>
        )}
      </div>
      {countedAs && (
        <div className="mt-1 text-xs text-muted" title="Counted as">
          {countedAs}
        </div>
      )}
    </div>
  );
}

/** The face crop, and the one click that matters: an unmarked face is
 *  marked as staff, a marked one is offered for unmarking. The image is
 *  fetched to an object URL and never cached by the browser. */
function FaceCrop({
  branchId,
  track,
  busy,
  onMark,
  onUnmark,
}: {
  branchId: string;
  track: PersonTrack;
  busy: boolean;
  onMark: (track: PersonTrack) => void;
  onUnmark: (track: PersonTrack) => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(null);
    setFailed(false);
    if (!track.face_crop_available) return;

    let active = true;
    let objectUrl: string | null = null;
    api
      .getTrackFaceCrop(branchId, track)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) setSrc(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    branchId,
    track.camera_id,
    track.face_crop_available,
    track.run_date,
    track.tracker_id,
  ]);

  if (!track.face_crop_available) {
    return <span className="text-xs text-muted">No face</span>;
  }
  if (failed) {
    return <span className="text-xs text-danger">Unavailable</span>;
  }
  if (!src) {
    return <span className="text-xs text-muted">Loading…</span>;
  }
  const isStaff = track.staff_id !== null;
  const canMark = track.face_embedding_available;
  const title = isStaff
    ? `${track.staff_name ?? "Staff"} — click to unmark`
    : canMark
      ? "Click to mark this person as staff (every processed day)"
      : "Open face crop";
  const ring = isStaff
    ? "border-accent ring-2 ring-accent/40"
    : canMark
      ? "border-border hover:border-accent hover:ring-2 hover:ring-accent/30"
      : "border-border";
  return (
    <button
      type="button"
      className={`relative block rounded-md border ${ring} transition disabled:cursor-wait disabled:opacity-60`}
      title={title}
      disabled={busy || (!isStaff && !canMark)}
      onClick={() => (isStaff ? onUnmark(track) : canMark ? onMark(track) : window.open(src))}
    >
      <img
        src={src}
        alt={`Face crop for track ${track.tracker_id}`}
        className="h-16 w-16 rounded-md object-cover"
      />
      {isStaff && (
        <span
          aria-hidden="true"
          className="absolute -top-1.5 -right-1.5 rounded-full bg-accent px-1.5 text-[10px] font-semibold text-white dark:text-[#0c1626]"
        >
          staff
        </span>
      )}
    </button>
  );
}

function Totals({ totals }: { totals: TrackDayTotals }) {
  const cells: { label: string; value: number; unit: string; hint?: string; accent?: boolean }[] = [
    { label: "Entries", value: totals.entries, unit: "people", hint: "everyone counted" },
    { label: "Exits", value: totals.exits, unit: "people" },
    {
      label: "Staff entries",
      value: totals.staff_entries,
      unit: "people",
      hint: `${formatCount(Math.max(0, totals.entries - totals.staff_entries))} customers`,
      accent: true,
    },
    { label: "Staff exits", value: totals.staff_exits, unit: "people", accent: true },
    { label: "Staff tracks", value: totals.staff_tracks, unit: "tracks", hint: "linked to the roster", accent: true },
  ];
  return (
    <div className="mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
      {cells.map((cell) => (
        <div
          key={cell.label}
          className={`${ui.card} px-3 py-2 text-xs ${cell.accent ? "border-accent/40" : ""}`}
        >
          <div className={ui.label}>{cell.label}</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold leading-tight tabular-nums">
              {formatCount(cell.value)}
            </span>
            <span className="text-muted">{cell.unit}</span>
          </div>
          {cell.hint && <div className="text-muted">{cell.hint}</div>}
        </div>
      ))}
    </div>
  );
}

export function TracksPanel({
  branch,
  onClose,
}: {
  branch: Branch;
  onClose: () => void;
}) {
  const [day, setDay] = useState(() => urlParam("track_day") ?? "");
  const [cameraId, setCameraId] = useState(
    () => urlParam("track_camera") ?? "",
  );
  const [page, setPage] = useState(() =>
    Math.max(0, (Number(urlParam("track_page")) || 1) - 1),
  );
  const [busyTrack, setBusyTrack] = useState<string | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [staffStatus, setStaffStatus] = useTransientStatus(9000);

  useUrlState(
    {
      track_day: day,
      track_camera: cameraId,
      track_page: page === 0 ? "" : String(page + 1),
    },
    (params) => {
      if ("track_day" in params) setDay(params.track_day);
      if ("track_camera" in params) setCameraId(params.track_camera);
      if ("track_page" in params) {
        setPage(Math.max(0, (Number(params.track_page) || 1) - 1));
      }
    },
  );

  const result = useAsync(
    () =>
      api.listTracks(branch.id, {
        day: day || undefined,
        cameraId: cameraId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    [branch.id, day, cameraId, page],
  );
  const response = result.data;
  const tracks = response?.tracks ?? [];

  // With no date in the URL, the API chooses the branch's latest recorded
  // day. Persist that choice so a copied link restores the exact view.
  useEffect(() => {
    if (!day && response?.day) setDay(response.day);
  }, [day, response?.day]);

  useEffect(() => {
    if (
      cameraId &&
      response &&
      !response.camera_ids.includes(cameraId)
    ) {
      setCameraId("");
      setPage(0);
    }
  }, [cameraId, response]);

  const total = response?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedDay = day || response?.day || "";

  useEffect(() => {
    const lastPage = Math.max(0, pageCount - 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, pageCount]);

  const trackKey = (track: PersonTrack) =>
    `${track.run_date}:${track.camera_id}:${track.tracker_id}`;

  const markAsStaff = async (track: PersonTrack) => {
    setBusyTrack(trackKey(track));
    setStaffError(null);
    try {
      const { staff, applied } = await api.markTrackAsStaff(branch.id, track, {
        name: "",
        note: "",
      });
      const days = applied.days.length;
      setStaffStatus(
        `${staff.name} marked as staff: ${formatCount(applied.tracks)} track${
          applied.tracks === 1 ? "" : "s"
        } linked across ${days} processed day${days === 1 ? "" : "s"} — ` +
          `${formatCount(applied.entries)} entries and ${formatCount(applied.exits)} exits ` +
          "now count as staff. Future runs recognise this face on their own. " +
          "Rename them in the Staff panel.",
      );
      result.reload();
    } catch (err) {
      setStaffError(errorMessage(err));
    } finally {
      setBusyTrack(null);
    }
  };

  const unmark = async (track: PersonTrack) => {
    if (track.staff_id === null) return;
    const who = track.staff_name ?? `Staff #${track.staff_id}`;
    const confirmed = window.confirm(
      `Unmark ${who}?\n\nTheir enrollment, stored face signature and crop are permanently ` +
        "deleted (the consent-withdrawal path); every track linked to them is unlinked " +
        "and their entries count as customers again.",
    );
    if (!confirmed) return;
    setBusyTrack(trackKey(track));
    setStaffError(null);
    try {
      const applied = await api.deleteStaff(branch.id, track.staff_id);
      setStaffStatus(
        `${who} unmarked: ${formatCount(applied.tracks)} track${
          applied.tracks === 1 ? "" : "s"
        } unlinked, ${formatCount(applied.entries)} entries count as customers again.`,
      );
      result.reload();
    } catch (err) {
      setStaffError(errorMessage(err));
    } finally {
      setBusyTrack(null);
    }
  };

  return (
    <Panel
      title={`Tracks — ${branch.name}`}
      subtitle={
        "Individual camera-local tracks from processed footage, with the " +
        "best face of each. Click a face to mark that person as staff: every " +
        "track that is the same person, on every processed day, is linked at " +
        "once and their entries move into the staff count — no re-run. " +
        "Nothing is removed from the totals; the dashboard's Staff toggle " +
        "shows them with or without staff. Click a marked face to unmark."
      }
      actions={
        <button type="button" className={ui.btnQuiet} onClick={onClose}>
          Close
        </button>
      }
    >
      {staffStatus && (
        <p className={`${ui.notice} ${ui.noticeSuccess}`}>{staffStatus}</p>
      )}
      {staffError && (
        <p className={`${ui.notice} ${ui.noticeError}`}>{staffError}</p>
      )}
      <div className={`${ui.toolbar} mb-3.5`}>
        <label className={ui.field}>
          Processed day
          <Select
            value={selectedDay}
            onChange={(event) => {
              setDay(event.target.value);
              setCameraId("");
              setPage(0);
            }}
            disabled={(response?.available_days.length ?? 0) === 0}
          >
            {!selectedDay && <option value="">No recorded days</option>}
            {selectedDay && !response?.available_days.includes(selectedDay) && (
              <option value={selectedDay}>{selectedDay}</option>
            )}
            {(response?.available_days ?? []).map((availableDay) => (
              <option key={availableDay} value={availableDay}>
                {availableDay}
              </option>
            ))}
          </Select>
        </label>
        <label className={`${ui.field} min-w-[180px]`}>
          Camera
          <Select
            value={cameraId}
            onChange={(event) => {
              setCameraId(event.target.value);
              setPage(0);
            }}
          >
            <option value="">All cameras</option>
            {(response?.camera_ids ?? []).map((id) => (
              <option key={id} value={id}>
                {cameraLabel(id)}
              </option>
            ))}
          </Select>
        </label>
        <div className="ml-auto self-end text-sm text-muted">
          {formatCount(total)} track{total === 1 ? "" : "s"}
        </div>
      </div>

      {response?.totals && <Totals totals={response.totals} />}

      <AsyncBlock
        loading={result.loading}
        error={result.error}
        empty={tracks.length === 0}
        emptyMessage={
          response?.available_days.length
            ? "No tracks match this day and camera."
            : "No tracks have been recorded for this branch yet. Process a " +
              "footage day first."
        }
      >
        <div className="overflow-x-auto">
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Track</th>
                <th>Visible</th>
                <th>Observations</th>
                <th>Crossing</th>
                <th>Best observation</th>
                <th>Face</th>
                <th>Staff</th>
                <th>Embeddings</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((track) => {
                const busy = busyTrack === trackKey(track);
                return (
                  <tr key={`${track.camera_id}:${track.tracker_id}`}>
                    <td className="whitespace-nowrap">
                      <strong>#{track.tracker_id}</strong>
                      <div className="text-xs text-muted">
                        {cameraLabel(track.camera_id)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {timeOnly(track.started_at)}–{timeOnly(track.ended_at)}
                      <div className="text-xs text-muted">
                        {formatDuration(track.started_at, track.ended_at)}
                      </div>
                    </td>
                    <td>
                      {formatCount(track.observations)}
                      <div className="text-xs text-muted">
                        frames {track.first_frame}–{track.last_frame}
                      </div>
                    </td>
                    <td>
                      <Crossing track={track} />
                    </td>
                    <td>
                      <span className="font-mono text-xs">{track.best_file}</span>
                      <div
                        className="text-xs text-muted"
                        title={`bbox ${track.best_bbox.join(", ")}`}
                      >
                        frame {track.best_frame} · conf{" "}
                        {Math.round(track.best_confidence * 100)}%
                      </div>
                    </td>
                    <td>
                      <FaceCrop
                        branchId={branch.id}
                        track={track}
                        busy={busy}
                        onMark={markAsStaff}
                        onUnmark={unmark}
                      />
                      {track.face_crop_available && (
                        <div className="mt-1 text-xs text-muted">
                          {track.face_crop_width}×{track.face_crop_height} · conf{" "}
                          {Math.round((track.face_confidence ?? 0) * 100)}%
                        </div>
                      )}
                    </td>
                    <td>
                      {track.staff_id !== null ? (
                        <>
                          <span className={`${ui.pill} ${ui.pillSucceeded}`}>
                            {track.staff_name ?? `Staff #${track.staff_id}`}
                          </span>
                          <div className="mt-1 text-xs text-muted">
                            {track.staff_similarity != null && (
                              <>{Math.round(track.staff_similarity * 100)}% match · </>
                            )}
                            <button
                              type="button"
                              className="underline decoration-dotted hover:text-text disabled:opacity-60"
                              disabled={busy}
                              onClick={() => unmark(track)}
                            >
                              Unmark
                            </button>
                          </div>
                        </>
                      ) : track.face_embedding_available ? (
                        <button
                          type="button"
                          className={ui.btnQuiet}
                          disabled={busy}
                          onClick={() => markAsStaff(track)}
                        >
                          {busy ? "Marking…" : "Mark as staff"}
                        </button>
                      ) : (
                        <span className="text-xs text-muted">Face required</span>
                      )}
                    </td>
                    <td>
                      {track.embedding_available ? (
                        <>
                          <div className="text-xs text-muted">Appearance</div>
                          <span className={`${ui.pill} ${ui.pillSucceeded}`}>
                            {track.embedding_dim} dims
                          </span>
                          <div
                            className="mt-1 max-w-[220px] truncate text-xs text-muted"
                            title={track.embedding_model}
                          >
                            {track.embedding_model}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted">missing</span>
                      )}
                      <div className="mt-2 border-t border-border pt-2">
                        <div className="text-xs text-muted">Face</div>
                        {track.face_embedding_available ? (
                          <>
                            <span className={`${ui.pill} ${ui.pillSucceeded}`}>
                              {track.face_embedding_dim} dims
                            </span>
                            <div
                              className="mt-1 max-w-[220px] truncate text-xs text-muted"
                              title={track.face_embedding_model ?? undefined}
                            >
                              {track.face_embedding_model}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-muted">No usable face</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div className="mt-3 flex items-center justify-end gap-2 text-[13px] text-muted">
            <span>
              {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, total)} of{" "}
              {formatCount(total)}
            </span>
            <button
              type="button"
              className={ui.btnQuiet}
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              ← Prev
            </button>
            <button
              type="button"
              className={ui.btnQuiet}
              disabled={page + 1 >= pageCount}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </AsyncBlock>
    </Panel>
  );
}
